import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack, TextField, Grid, Button, Divider, Typography, MenuItem } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { LoadingButton } from '@mui/lab';
import tracker from '../../../api/tracker';
import { PRINT_SERIF, printDocument } from '../../../components/print/printDocument';

const LANGUAGE_OPTIONS = ['English', 'Spanish', 'Bilingual'];

function Field({ label, register, name, type = 'text' }) {
  return (
    <TextField
      label={label}
      type={type}
      fullWidth
      size="small"
      InputLabelProps={type === 'date' || type === 'time' ? { shrink: true } : {}}
      {...register(name)}
    />
  );
}

function PrintField({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px', gap: '8px' }}>
      <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{label}:</span>
      <span style={{ flex: 1, borderBottom: '1px solid #000', minWidth: '100px', paddingBottom: '2px' }}>
        {value || ''}
      </span>
    </div>
  );
}

function PrintRow({ fields }) {
  return (
    <div style={{ display: 'flex', gap: '24px', marginBottom: '10px' }}>
      {fields.map(({ label, value, flex = 1 }) => (
        <div key={label} style={{ flex, display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{label}:</span>
          <span style={{ flex: 1, borderBottom: '1px solid #000', paddingBottom: '2px' }}>{value || ''}</span>
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ textAlign: 'center', margin: '16px 0 12px' }}>
      <span style={{ fontWeight: 700, textDecoration: 'underline', fontSize: '14px', textTransform: 'uppercase' }}>
        {title}
      </span>
    </div>
  );
}

export default function FuneralFormSection({ funeralForm }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      deceased_name: '',
      deceased_address: '',
      date_of_death: '',
      age: '',
      next_of_kin: '',
      relationship: '',
      family_address: '',
      family_phone: '',
      funeral_home_name: '',
      funeral_home_address: '',
      funeral_home_contact_name: '',
      funeral_home_phone: '',
      wake_date: '',
      wake_time: '',
      cemetery: '',
      mass_date: '',
      mass_time: '',
      organist: '',
      organist_phone: '',
      language: '',
    },
  });

  const values = watch();

  useEffect(() => {
    if (funeralForm) {
      reset({
        deceased_name: funeralForm.deceased_name || '',
        deceased_address: funeralForm.deceased_address || '',
        date_of_death: funeralForm.date_of_death || '',
        age: funeralForm.age || '',
        next_of_kin: funeralForm.next_of_kin || '',
        relationship: funeralForm.relationship || '',
        family_address: funeralForm.family_address || '',
        family_phone: funeralForm.family_phone || '',
        funeral_home_name: funeralForm.funeral_home_name || '',
        funeral_home_address: funeralForm.funeral_home_address || '',
        funeral_home_contact_name: funeralForm.funeral_home_contact_name || '',
        funeral_home_phone: funeralForm.funeral_home_phone || '',
        wake_date: funeralForm.wake_date || '',
        wake_time: funeralForm.wake_time || '',
        cemetery: funeralForm.cemetery || '',
        mass_date: funeralForm.mass_date || '',
        mass_time: funeralForm.mass_time || '',
        organist: funeralForm.organist || '',
        organist_phone: funeralForm.organist_phone || '',
        language: funeralForm.language || '',
      });
    }
  }, [funeralForm, reset]);

  const onSubmit = async (data) => {
    if (funeralForm) {
      await tracker.post(`funerals/${funeralForm.hashid}`, data);
    } else {
      await tracker.post('funerals', data);
    }
    navigate('/dashboard/funeral');
  };

  return (
    <>
      {/* ── Screen: editable form ── */}
      <form onSubmit={handleSubmit(onSubmit)} className="no-print">
        <Stack spacing={3}>
          {/* Deceased Information */}
          <Typography variant="subtitle1" fontWeight={700}>{t('section_deceased_info')}</Typography>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label={t('name_of_deceased')} register={register} name="deceased_name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('Address')} register={register} name="deceased_address" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label={t('date_of_death')} register={register} name="date_of_death" type="date" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label={t('Age')} register={register} name="age" type="number" />
            </Grid>
          </Grid>

          {/* Family */}
          <Typography variant="subtitle1" fontWeight={700}>{t('section_family')}</Typography>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label={t('next_of_kin')} register={register} name="next_of_kin" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('relationship')} register={register} name="relationship" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('Address')} register={register} name="family_address" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('Phone')} register={register} name="family_phone" />
            </Grid>
          </Grid>

          {/* Funeral Home */}
          <Typography variant="subtitle1" fontWeight={700}>{t('section_funeral_home')}</Typography>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label={t('funeral_home_name')} register={register} name="funeral_home_name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('Address')} register={register} name="funeral_home_address" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('funeral_home_contact_name')} register={register} name="funeral_home_contact_name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('Phone')} register={register} name="funeral_home_phone" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label={t('wake_date')} register={register} name="wake_date" type="date" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label={t('wake_time')} register={register} name="wake_time" type="time" />
            </Grid>
          </Grid>

          {/* Funeral Mass */}
          <Typography variant="subtitle1" fontWeight={700}>{t('section_funeral_mass')}</Typography>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label={t('cemetery')} register={register} name="cemetery" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Field label={t('mass_date')} register={register} name="mass_date" type="date" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Field label={t('mass_time')} register={register} name="mass_time" type="time" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('organist')} register={register} name="organist" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={t('organist_phone')} register={register} name="organist_phone" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label={t('Language')} fullWidth size="small" defaultValue="" {...register('language')}>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={printDocument}>
              {t('print')}
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              {t('Save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </form>

      {/* ── Print-only layout ── rendered at body level via Portal ── */}
      {createPortal(<>
      <div className="funeral-print-only">
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px', fontFamily: PRINT_SERIF, fontSize: '13px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '1px' }}>
            CHURCH OF THE HOLY SPIRIT
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>
            FUNERAL FORM
          </div>
        </div>

        <SectionHeader title="Deceased Information" />
        <PrintField label="Name of Deceased" value={values.deceased_name} />
        <PrintField label="Address" value={values.deceased_address} />
        <PrintRow fields={[
          { label: 'Date of Death', value: values.date_of_death },
          { label: 'Age', value: values.age, flex: 0.5 },
        ]} />

        <SectionHeader title="Family" />
        <PrintRow fields={[
          { label: 'Next of Kin', value: values.next_of_kin },
          { label: 'Relationship', value: values.relationship, flex: 0.6 },
        ]} />
        <PrintRow fields={[
          { label: 'Address', value: values.family_address },
          { label: 'Phone', value: values.family_phone, flex: 0.6 },
        ]} />

        <SectionHeader title="Funeral Home" />
        <PrintField label="Funeral Name" value={values.funeral_home_name} />
        <PrintField label="Address" value={values.funeral_home_address} />
        <PrintRow fields={[
          { label: 'Name', value: values.funeral_home_contact_name },
          { label: 'Phone', value: values.funeral_home_phone },
        ]} />
        <PrintRow fields={[
          { label: 'Wake', value: values.wake_date },
          { label: 'Time', value: values.wake_time },
        ]} />

        <SectionHeader title="Funeral Mass" />
        <PrintRow fields={[
          { label: 'Cemetery', value: values.cemetery },
          { label: 'Date', value: values.mass_date, flex: 0.5 },
          { label: 'Time', value: values.mass_time, flex: 0.4 },
        ]} />
        <PrintRow fields={[
          { label: 'Organist', value: values.organist },
          { label: 'Phone', value: values.organist_phone },
        ]} />
        <PrintField label="Language" value={values.language} />

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '40px', fontSize: '11px' }}>
          1940 University Avenue, Bronx NY, 10453 &nbsp; Tel: 718-583-0120 &nbsp; Fax: 718-583-1444
        </div>
        </div>
      </div>

      <style>{`
        .funeral-print-only { display: none; }
        @media print {
          body > *:not(.funeral-print-only) { display: none !important; }
          .funeral-print-only { display: block !important; }
        }
      `}</style>
      </>, document.body)}
    </>
  );
}
